<script setup lang="ts">
import { computed, toRef } from 'vue'
import { isUndefined } from 'lodash'

import type { ISiteIdentifier } from '@/components/rc/common/RcSiteSelect.vue'
import useProgramEarningsService, { MISOProgram } from '@/composables/eos/program-earnings-dashboard'

import { months } from '@/helpers'
import { downloadFile } from '@/util'
import { formatCurrency } from '@/util/formatters'

const tabs = [
  { label: 'All Programs', name: MISOProgram.All },
  { label: 'Day Ahead Price Response', name: MISOProgram.DAPR },
  { label: 'Operating Reserves', name: MISOProgram.OR },
]

// These are just forwarded to the query
const props = defineProps<{
  siteSelected?: ISiteIdentifier
  yearSelected?: number
}>()

const monthsOptions = computed(() =>
  months.filter((_, index) => {
    // Only show months up to the current month for the current year
    if (!props.yearSelected) return true
    const now = new Date()
    return props.yearSelected < now.getFullYear() || index <= now.getMonth()
  })
)

const monthSelected = defineModel<number>('monthSelected')
const activeTab = defineModel<MISOProgram>('activeTab', { required: true })

const { monthEarningsSummaryQuery, exportMonthlyEarningsMutation } = useProgramEarningsService()

// Binds to the same query the parent does

const { data, error, refetch, isLoading } = monthEarningsSummaryQuery(
  toRef(props, 'siteSelected'),
  activeTab,
  toRef(props, 'yearSelected'),
  monthSelected
)

// Derive display values from the raw query result, applying formatting and fallbacks as needed.

const totalEarnings = computed(() => formatCurrency(data.value?.total_earnings_usd, 2, false))

const participationHours = computed(() => data.value?.participation_hours ?? '-')

const avgHourlyEarning = computed(() => formatCurrency(data.value?.avg_hourly_earnings_usd, 3, false))

async function handleExport() {
  if (!props.siteSelected?.site_id || !props.yearSelected || isUndefined(monthSelected.value)) return
  const mm = String(monthSelected.value + 1).padStart(2, '0')
  const blob = await exportMonthlyEarningsMutation.mutateAsync({
    siteId: props.siteSelected.site_id,
    program: activeTab.value,
    year: props.yearSelected,
    month: monthSelected.value,
  })
  downloadFile(blob, `program-earnings-${props.siteSelected.site_id}-${props.yearSelected}-${mm}.csv`)
}
</script>

<template>
  <el-card>
    <!-- Card top -->
    <div class="flex items-center justify-between mb-6">
      <!-- Tab label -->
      <span class="uppercase text-xs">{{ tabs.find((t) => t.name === activeTab)!.label }}</span>

      <!-- Month picker -->
      <div class="flex gap-4">
        <el-select v-model="monthSelected" class="w-30" :disabled="exportMonthlyEarningsMutation.isPending.value">
          <el-option v-for="(month, index) in monthsOptions" :key="month" :label="month" :value="index" />
        </el-select>
        <el-button
          :loading="exportMonthlyEarningsMutation.isPending.value"
          :disabled="isUndefined(monthSelected) || exportMonthlyEarningsMutation.isPending.value"
          @click="handleExport"
          >Export</el-button
        >
      </div>
    </div>

    <template v-if="isUndefined(monthSelected)">
      <div class="h-16 flex items-center justify-center">
        <span class="text-gray-500">Please select a month.</span>
      </div>
    </template>

    <!-- Loading -->
    <template v-else-if="isLoading">
      <div class="h-16 flex items-center justify-center">
        <span class="text-gray-500">Loading...</span>
      </div>
    </template>

    <!-- Error -->
    <template v-else-if="error">
      <rl-error-result :error="error">
        <el-button @click="refetch()">
          <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
        </el-button>
      </rl-error-result>
    </template>

    <template v-else>
      <!-- Summary values -->
      <div class="h-16 grid grid-cols-3 gap-8">
        <!-- Total earnings -->
        <div>
          <span class="text-text-tertiary text-sm">Total Earnings</span>
          <div class="flex gap-4">
            <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
              >$ <span class="text-text-primary font-semibold">{{ totalEarnings }}</span></span
            >
          </div>
        </div>

        <!-- Participation hours -->
        <div>
          <span class="text-text-tertiary text-sm">Participation Hours</span>
          <div class="flex gap-4">
            <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
              ><span class="text-text-primary font-semibold pr-1">{{ participationHours }}</span
              >h
            </span>
          </div>
        </div>

        <!-- Average hourly earning -->
        <div>
          <span class="text-text-tertiary text-sm">Average Hourly Earnings</span>
          <div class="flex gap-4">
            <span class="text-text-tertiary text-2xl w-max whitespace-nowrap"
              ><span class="text-text-primary font-semibold pr-1">{{ avgHourlyEarning }}</span
              >$/h
            </span>
          </div>
        </div>
      </div>
    </template>
  </el-card>
</template>
